import { supabase } from '../lib/supabase';

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
}

function toSnakeCase(obj) {
  if (!obj) return obj;
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
}

/**
 * Calculate stars earned based on quiz/game scores in answers_data
 * 1 star = completed, 2 stars = 70%+ combined score, 3 stars = 100% perfect score
 */
export function calculateStarsEarned(answersData) {
  if (!answersData) return 1; // base star for completion

  const quizScores = answersData?.quiz_scores || [];
  const gameScores = answersData?.game_scores || [];

  const hasQuizzes = quizScores?.length > 0;
  const hasGames = gameScores?.length > 0;

  if (!hasQuizzes && !hasGames) return 1;

  let totalPoints = 0;
  let earnedPoints = 0;

  // Each quiz question counts as 1 point
  quizScores?.forEach(q => {
    totalPoints += 1;
    if (q?.correct === true || q?.correct === 1) earnedPoints += 1;
  });

  // Each game counts as 1 point (completed correctly = 1, failed = 0)
  gameScores?.forEach(g => {
    if (g?.total !== undefined && (g?.score !== undefined || g?.correct !== undefined)) {
      const totalPts = Number(g?.total) || 1;
      // Prefer 'correct' (raw count) over 'score' (which may be a ratio like 0.5)
      const earnedPts = g?.correct !== undefined
        ? Number(g?.correct)
        : Number(g?.score) * totalPts;
      totalPoints += totalPts;
      earnedPoints += earnedPts;
    } else if (g?.percentage !== undefined) {
      totalPoints += 100;
      earnedPoints += Number(g?.percentage) || 0;
    } else {
      // Fallback: treat as 1 point, correct if score truthy
      totalPoints += 1;
      earnedPoints += (g?.correct === true || g?.correct === 1) ? 1 : 0;
    }
  });

  if (totalPoints === 0) return 1;

  const percentage = (earnedPoints / totalPoints) * 100;

  if (percentage >= 100) return 3;
  if (percentage >= 70) return 2;
  return 1;
}

/**
 * Award stars to a student when they complete a trail
 */
export const awardStarsForTrailCompletion = async (studentId, trailId, answersData) => {
  try {
    const starsEarned = calculateStarsEarned(answersData);

    // Read the previous stars BEFORE updating, to calculate the diff correctly
    const { data: progressData } = await supabase?.from('student_progress')?.select('stars_earned')?.eq('student_id', studentId)?.eq('trail_id', trailId)?.single();
    const previousStars = progressData?.stars_earned || 0;

    // Update stars_earned in student_progress
    const { error: progressError } = await supabase?.from('student_progress')?.update({ stars_earned: starsEarned })?.eq('student_id', studentId)?.eq('trail_id', trailId);

    if (progressError) {
      console.error('Error updating stars in progress:', progressError);
    }

    // Upsert student_rewards record
    const { data: existing } = await supabase?.from('student_rewards')?.select('*')?.eq('student_id', studentId)?.single();

    if (existing) {
      // Only add the difference to avoid double-awarding
      const starsDiff = starsEarned - previousStars;

      if (starsDiff > 0) {
        const newTotal = (existing?.total_stars || 0) + starsDiff;
        const newAvailable = (existing?.stars_available || 0) + starsDiff;
        await supabase?.from('student_rewards')?.update({
            total_stars: newTotal,
            stars_available: newAvailable,
            updated_at: new Date()?.toISOString()
          })?.eq('student_id', studentId);
      }
    } else {
      // Create new record
      await supabase?.from('student_rewards')?.insert({
          student_id: studentId,
          total_stars: starsEarned,
          stars_spent: 0,
          stars_available: starsEarned
        });
    }

    return { starsEarned, error: null };
  } catch (error) {
    console.error('awardStarsForTrailCompletion error:', error);
    return { starsEarned: 0, error: error?.message };
  }
};

/**
 * Get student reward balance
 */
export const getStudentRewards = async (studentId) => {
  try {
    const { data, error } = await supabase?.from('student_rewards')?.select('*')?.eq('student_id', studentId)?.single();

    if (error && error?.code === 'PGRST116') {
      // No record yet
      return { rewards: { totalStars: 0, starsSpent: 0, starsAvailable: 0 }, error: null };
    }
    if (error) {
      return { rewards: null, error: error?.message };
    }

    return { rewards: toCamelCase(data), error: null };
  } catch (error) {
    console.error('getStudentRewards error:', error);
    return { rewards: null, error: error?.message };
  }
};

/**
 * Get all active rewards for a student's classroom teacher
 */
export const getAvailableRewards = async (teacherId) => {
  try {
    let query = supabase?.from('rewards_catalog')?.select('*')?.eq('is_active', true)?.order('star_cost', { ascending: true });

    if (teacherId) {
      query = query?.eq('teacher_id', teacherId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('getAvailableRewards error:', error);
      return { rewards: [], error: error?.message };
    }

    return { rewards: data?.map(toCamelCase) || [], error: null };
  } catch (error) {
    console.error('getAvailableRewards error:', error);
    return { rewards: [], error: error?.message };
  }
};

/**
 * Get all rewards for teacher management
 */
export const getTeacherRewards = async (teacherId) => {
  try {
    const { data, error } = await supabase?.from('rewards_catalog')?.select('*')?.eq('teacher_id', teacherId)?.order('created_at', { ascending: false });

    if (error) {
      return { rewards: [], error: error?.message };
    }

    return { rewards: data?.map(toCamelCase) || [], error: null };
  } catch (error) {
    console.error('getTeacherRewards error:', error);
    return { rewards: [], error: error?.message };
  }
};

/**
 * Create a new reward
 */
export const createReward = async (teacherId, rewardData) => {
  try {
    const { data, error } = await supabase?.from('rewards_catalog')?.insert({
        teacher_id: teacherId,
        title: rewardData?.title,
        description: rewardData?.description || '',
        star_cost: rewardData?.starCost || rewardData?.star_cost || 5,
        category: rewardData?.category || 'general',
        icon: rewardData?.icon || 'Gift',
        is_active: rewardData?.isActive !== undefined ? rewardData?.isActive : true
      })?.select()?.single();

    if (error) {
      return { reward: null, error: error?.message };
    }

    return { reward: toCamelCase(data), error: null };
  } catch (error) {
    console.error('createReward error:', error);
    return { reward: null, error: error?.message };
  }
};

/**
 * Update a reward
 */
export const updateReward = async (rewardId, updates) => {
  try {
    const snakeUpdates = toSnakeCase(updates);
    snakeUpdates.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('rewards_catalog')?.update(snakeUpdates)?.eq('id', rewardId)?.select()?.single();

    if (error) {
      return { reward: null, error: error?.message };
    }

    return { reward: toCamelCase(data), error: null };
  } catch (error) {
    console.error('updateReward error:', error);
    return { reward: null, error: error?.message };
  }
};

/**
 * Delete a reward
 */
export const deleteReward = async (rewardId) => {
  try {
    const { error } = await supabase?.from('rewards_catalog')?.delete()?.eq('id', rewardId);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    console.error('deleteReward error:', error);
    return { error: error?.message };
  }
};

/**
 * Redeem a reward for a student
 */
export const redeemReward = async (studentId, rewardId, starCost) => {
  try {
    // Check student has enough stars
    const { rewards } = await getStudentRewards(studentId);
    if (!rewards || rewards?.starsAvailable < starCost) {
      return { redemption: null, error: 'Not enough stars to redeem this reward' };
    }

    // Create redemption record
    const { data: redemption, error: redemptionError } = await supabase?.from('reward_redemptions')?.insert({
        student_id: studentId,
        reward_id: rewardId,
        stars_spent: starCost,
        status: 'pending'
      })?.select()?.single();

    if (redemptionError) {
      return { redemption: null, error: redemptionError?.message };
    }

    // Deduct stars from student_rewards
    const { error: updateError } = await supabase?.from('student_rewards')?.update({
        stars_spent: (rewards?.starsSpent || 0) + starCost,
        stars_available: (rewards?.starsAvailable || 0) - starCost,
        updated_at: new Date()?.toISOString()
      })?.eq('student_id', studentId);

    if (updateError) {
      console.error('Error updating star balance:', updateError);
    }

    return { redemption: toCamelCase(redemption), error: null };
  } catch (error) {
    console.error('redeemReward error:', error);
    return { redemption: null, error: error?.message };
  }
};

/**
 * Get redemption history for a student
 */
export const getStudentRedemptions = async (studentId) => {
  try {
    const { data, error } = await supabase?.from('reward_redemptions')?.select(`
        *,
        rewards_catalog (
          title,
          description,
          icon,
          category,
          star_cost
        )
      `)?.eq('student_id', studentId)?.order('redeemed_at', { ascending: false });

    if (error) {
      return { redemptions: [], error: error?.message };
    }

    const mapped = data?.map(r => ({
      ...toCamelCase(r),
      reward: r?.rewards_catalog ? toCamelCase(r?.rewards_catalog) : null
    })) || [];

    return { redemptions: mapped, error: null };
  } catch (error) {
    console.error('getStudentRedemptions error:', error);
    return { redemptions: [], error: error?.message };
  }
};

/**
 * Get all pending redemptions for a teacher
 */
export const getTeacherRedemptions = async (teacherId) => {
  try {
    // Get all rewards for this teacher first
    const { data: rewardIds } = await supabase?.from('rewards_catalog')?.select('id')?.eq('teacher_id', teacherId);

    if (!rewardIds || rewardIds?.length === 0) {
      return { redemptions: [], error: null };
    }

    const ids = rewardIds?.map(r => r?.id);

    const { data, error } = await supabase?.from('reward_redemptions')?.select(`
        *,
        rewards_catalog (
          title,
          description,
          icon,
          category,
          star_cost
        ),
        students (
          full_name,
          username
        )
      `)?.in('reward_id', ids)?.order('redeemed_at', { ascending: false });

    if (error) {
      return { redemptions: [], error: error?.message };
    }

    const mapped = data?.map(r => ({
      ...toCamelCase(r),
      reward: r?.rewards_catalog ? toCamelCase(r?.rewards_catalog) : null,
      student: r?.students ? toCamelCase(r?.students) : null
    })) || [];

    return { redemptions: mapped, error: null };
  } catch (error) {
    console.error('getTeacherRedemptions error:', error);
    return { redemptions: [], error: error?.message };
  }
};

/**
 * Update redemption status (teacher action)
 */
export const updateRedemptionStatus = async (redemptionId, status, teacherNotes) => {
  try {
    const { data, error } = await supabase?.from('reward_redemptions')?.update({
        status,
        teacher_notes: teacherNotes || null,
        updated_at: new Date()?.toISOString()
      })?.eq('id', redemptionId)?.select()?.single();

    if (error) {
      return { redemption: null, error: error?.message };
    }

    // Refund stars when rejected
    if (status === 'rejected' && data?.student_id) {
      const studentId = data?.student_id;

      // Recalculate stars_spent from all non-rejected redemptions for this student
      const { data: activeRedemptions, error: redemptionsError } = await supabase
        ?.from('reward_redemptions')
        ?.select('stars_spent, status')
        ?.eq('student_id', studentId)
        ?.in('status', ['pending', 'approved', 'completed']);

      if (redemptionsError) {
        console.error('Error fetching active redemptions for refund:', redemptionsError);
      } else {
        const totalSpent = (activeRedemptions || [])?.reduce((sum, r) => sum + (r?.stars_spent || 0), 0);

        // Get total_stars from student_rewards
        const { data: studentRewards, error: rewardsError } = await supabase
          ?.from('student_rewards')
          ?.select('total_stars')
          ?.eq('student_id', studentId)
          ?.single();

        if (rewardsError) {
          console.error('Error fetching student_rewards for refund:', rewardsError);
        } else if (studentRewards) {
          const totalStars = studentRewards?.total_stars || 0;
          const newAvailable = Math.max(0, totalStars - totalSpent);

          const { error: updateError, count } = await supabase
            ?.from('student_rewards')
            ?.update({
              stars_spent: totalSpent,
              stars_available: newAvailable,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('student_id', studentId);

          if (updateError) {
            console.error('Error updating student_rewards after rejection:', updateError);
          }
        }
      }
    }

    return { redemption: toCamelCase(data), error: null };
  } catch (error) {
    console.error('updateRedemptionStatus error:', error);
    return { redemption: null, error: error?.message };
  }
};

/**
 * Get stars earned per trail for a student
 */
export const getStudentTrailStars = async (studentId) => {
  try {
    const { data, error } = await supabase?.from('student_progress')?.select('trail_id, stars_earned')?.eq('student_id', studentId);

    if (error) {
      return { trailStars: {}, error: error?.message };
    }

    const trailStars = {};
    data?.forEach(p => {
      trailStars[p.trail_id] = p?.stars_earned || 0;
    });

    return { trailStars, error: null };
  } catch (error) {
    console.error('getStudentTrailStars error:', error);
    return { trailStars: {}, error: error?.message };
  }
};

/**
 * Get redemption notifications for a teacher (for bell icon)
 */
export const getTeacherRedemptionNotifications = async (teacherId) => {
  try {
    const { data: rewardIds } = await supabase?.from('rewards_catalog')?.select('id')?.eq('teacher_id', teacherId);

    if (!rewardIds || rewardIds?.length === 0) {
      return { notifications: [], error: null };
    }

    const ids = rewardIds?.map(r => r?.id);

    const { data, error } = await supabase
      ?.from('reward_redemptions')
      ?.select(`
        id,
        stars_spent,
        status,
        redeemed_at,
        teacher_read_at,
        rewards_catalog (
          title,
          icon
        ),
        students (
          full_name,
          username
        )
      `)
      ?.in('reward_id', ids)
      ?.order('redeemed_at', { ascending: false })
      ?.limit(50);

    if (error) {
      return { notifications: [], error: error?.message };
    }

    const notifications = data?.map(r => ({
      id: r?.id,
      studentName: r?.students?.full_name || r?.students?.username || 'Unknown Student',
      rewardTitle: r?.rewards_catalog?.title || 'Unknown Reward',
      rewardIcon: r?.rewards_catalog?.icon || 'Gift',
      starsSpent: r?.stars_spent,
      status: r?.status,
      redeemedAt: r?.redeemed_at,
      isUnread: !r?.teacher_read_at
    })) || [];

    return { notifications, error: null };
  } catch (error) {
    console.error('getTeacherRedemptionNotifications error:', error);
    return { notifications: [], error: error?.message };
  }
};

/**
 * Mark all redemption notifications as read for a teacher
 */
export const markRedemptionNotificationsRead = async (teacherId) => {
  try {
    const { data: rewardIds } = await supabase?.from('rewards_catalog')?.select('id')?.eq('teacher_id', teacherId);

    if (!rewardIds || rewardIds?.length === 0) return { error: null };

    const ids = rewardIds?.map(r => r?.id);

    const { error } = await supabase
      ?.from('reward_redemptions')
      ?.update({ teacher_read_at: new Date()?.toISOString() })
      ?.in('reward_id', ids)
      ?.is('teacher_read_at', null);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    console.error('markRedemptionNotificationsRead error:', error);
    return { error: error?.message };
  }
};
